import { useLocation } from "react-router-dom";
import { MotionConfig, motion } from "framer-motion";
import { MainSidebar } from "@/modules/main-sidebar";
import { Header } from "@/modules/header";
import { FloatingChatWidget } from "@/modules/safecheck/components/messaging/FloatingChatWidget";
import { useMessagingOptional } from "@/modules/safecheck/messaging/MessagingProvider";
import { SidebarProvider, useSidebar } from "./SidebarProvider";
import { cn } from "@/shared/lib/cn";
import { motionTokens } from "@/shared/lib/motion";

type DashboardShellProps = {
  children: React.ReactNode;
};

function DashboardShellContent({ children }: DashboardShellProps) {
  const { isOpen, close } = useSidebar();
  const location = useLocation();
  const messaging = useMessagingOptional();
  const isOnMessagingPage = location.pathname === "/dashboard/messaging";
  const hasFloatingChat =
    Boolean(messaging) &&
    messaging.floatingState !== "hidden" &&
    !isOnMessagingPage;

  return (
    // reducedMotion="user" : déplacements coupés si le système demande moins de mouvement.
    <MotionConfig reducedMotion="user">
    <div className="flex min-h-screen">
      {isOpen && (
        <button
          type="button"
          aria-label="Close sidebar"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={close}
        />
      )}

      <MainSidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <Header />
        <main
          className={cn(
            "min-w-0 flex-1 px-3 pt-5 sm:px-5 md:px-6 lg:px-8 lg:pt-6",
            hasFloatingChat
              ? messaging?.floatingState === "expanded"
                ? "pb-[min(70vh,520px)] sm:pb-[min(70vh,520px)]"
                : "pb-24"
              : "pb-6 sm:pb-7 lg:pb-8",
          )}
        >
          {/* Transition de page : chaque nouvel écran apparaît en montant légèrement. */}
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: motionTokens.distance.sm }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: motionTokens.duration.normal, ease: motionTokens.easing.smooth }}
          >
            {children}
          </motion.div>
        </main>
      </div>
      <FloatingChatWidget />
    </div>
    </MotionConfig>
  );
}

export function DashboardShell({ children }: DashboardShellProps) {
  return (
    <SidebarProvider>
      <DashboardShellContent>{children}</DashboardShellContent>
    </SidebarProvider>
  );
}
